import { DatePipe } from '@angular/common'
import { Component, inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { Subject, Subscription } from 'rxjs'

import { SelectItem, SelectItemGroup } from 'primeng/api'
import { Button } from 'primeng/button'
import { Toolbar } from 'primeng/toolbar'

import { filter, map } from 'lodash-es'

import { ARCHIVE_DOCUMENT_STATES, ASSET_CATEGORIES, YESNO } from '@app/constants/constants'
import { WorkflowState } from '@app/interfaces/workflows'
import { ArchiveDocumentsApi } from '@app/apis/archive-documents.api'
import { PlacesApi } from '@app/apis/places.api'
import { LongRunningJobService } from '@app/services/long-running-job.service'
import { ConfigureFieldsComponent } from '@app/components/configure-fields/configure-fields'
import { ConfigureFiltersComponent } from '@app/components/configure-filters/configure-filters'
import { FlagComponent } from '@app/components/flags/flag.component'
import { StaticDropDown } from '@app/components/generic/generic'
import { GenericColumnComponent } from '@app/components/generic/generic-column'
import { GenericListComponent } from '@app/components/generic/generic-list'
import { compareStaticDropDownLabelFunction } from '@app/components/generic/utilities'
import { GlobalSearchComponent } from '@app/components/global-search/global-search'
import { WorkflowComponent } from '@app/components/workflows/workflow.component'
import { CountriesService } from '@app/generated/api/countries/countries.service'
import { ArchiveDocumentListItemState } from '@app/generated/api/model'
import type {
  ArchiveDocumentListItem, ArchiveDocumentType, CountriesListParams, CountryListItem, LongRunningJob, PaginatedArchiveDocumentCategoryList, PaginatedArchiveDocumentTypeList,
  PaginatedArchivesPlacesPlaceListList,
} from '@app/generated/api/model'

@Component({
  selector: 'app-search-list',
  styleUrl: 'search-list.component.css',
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'search-list.component.html',
  imports: [
    Toolbar,
    ConfigureFiltersComponent,
    Button,
    GlobalSearchComponent,
    ConfigureFieldsComponent,
    GenericListComponent,
    GenericColumnComponent,
    FlagComponent,
    WorkflowComponent,
    DatePipe,
  ],
})
export class SearchListComponent extends GenericListComponent<ArchiveDocumentListItem> implements OnInit {
  private readonly archiveDocumentsApi = inject(ArchiveDocumentsApi)
  private readonly placesApi = inject(PlacesApi)
  private readonly countriesService = inject(CountriesService)
  private readonly longRunningJobService = inject(LongRunningJobService)

  override tableData: ArchiveDocumentListItem[] = []
  tableReloadSubject: Subject<void> = new Subject<void>()
  override totalRecords: number = 0

  public documentTypeGroups: SelectItemGroup[] = []
  public allDocumentTypes: ArchiveDocumentType[] = []

  public countries: StaticDropDown[] = []
  public places: StaticDropDown[] = []
  public documentCategories: StaticDropDown[] = []
  public yesno: StaticDropDown[] = []
  public assetCategories: StaticDropDown[] = []
  public archiveDocumentWorkflowStates: StaticDropDown[] = ARCHIVE_DOCUMENT_STATES

  // get a handle on the underlying GenericListComponent in order to access its filters
  @ViewChild(GenericListComponent) genericListComponent!: GenericListComponent<ArchiveDocumentListItem>

  override ngOnInit () {
    this.api = this.archiveDocumentsApi
    this.detailCreateUrl = '/viewer/'
    this.loading = true

    this.loadStaticLists()
    this.loadCountries()
    this.loadPlaces()
    this.loadDocumentCategories()
    this.loadAllDocumentTypes()
    super.ngOnInit()
  }

  override returnTitle (): string {
    return $localize`:@@search-list-page-title:Acted Archives`
  }

  /**
   * change the list of visible columns according to the incoming 'profile'
   */
  changeVisibleColumns (profile: string) {
    const columnsHr = [
      'country__code_iso',
      'place__name',
      'type',
      'date_month',
      'landlord_code',
      'landlord__full_name',
      'third_party_code',
      'homere_code',
      'staff_name',
      'created_at',
      'state',
    ]
    const columnsFinance = [
      'country__code_iso',
      'place__name',
      'type',
      'date_month',
      'project_codes',
      'voucher_code',
      'balzac_book__saga_name',
      'budget_line',
      'created_at',
      'state',
    ]
    const columnsLog = [
      'country__code_iso',
      'place__name',
      'type',
      'date_month',
      'project_codes',
      'budget_line',
      'asset_code',
      'asset_categories',
      'warehouse_code',
      'supplier__code',
      'supplier__name',
      'document_number',
      'item_category',
      'order_form_date',
      'created_at',
      'state',
    ]
    const columnsProgramme = [
      'country__code_iso',
      'place__name',
      'type',
      'date_month',
      'project_codes',
      'activity',
      'created_at',
      'state',
    ]
    const goodColumns: GenericColumnComponent[] = filter(this.allColumns, (column) => {
      if (profile === 'finance') return columnsFinance.includes(column.key)
      if (profile === 'hr') return columnsHr.includes(column.key)
      if (profile === 'log') return columnsLog.includes(column.key)
      if (profile === 'programme') return columnsProgramme.includes(column.key)
      return true
    })
    this.visibleColumnKeys = map(goodColumns, (column) => {
      return column.key
    })
  }

  loadStaticLists () {
    this.yesno = YESNO
    this.assetCategories = ASSET_CATEGORIES
    this.assetCategories.sort(compareStaticDropDownLabelFunction(this.filterLabelField, this.locale))
  }

  /**
   * this function is shamelessly copied from `metadata.component.ts` and should be centralised somewhere, left like this as we
   * decide now to filter the results…
   */
  loadAllDocumentTypes () : Subscription {
    return this.archiveDocumentsApi.listDocTypes({ limit: 1000, ordering: 'label_en' }).subscribe((res: PaginatedArchiveDocumentTypeList) => {
      // populate global `allDocumentTypes` list, containing all the possible types and the fields they can contain
      this.allDocumentTypes = res.results

      // now build the grouped structure suitable for the prime-ng autocomplete widget
      // https://www.primefaces.org/primeng/autocomplete
      this.documentTypeGroups = []

      res.results.forEach(documentType => {
        // determine if a group exists already in our structure, if so get a handle on it
        let group = this.documentTypeGroups.find((existingGroups: SelectItemGroup) => {
          return documentType.department?.code === existingGroups.value
        })
        // if it didn't exist, create it and get a handle
        if (group === undefined) {
          const label = documentType.department ? documentType.department[this.filterLabelField] : $localize`:@@search-list-type-unknown:Unknown`
          const count = this.documentTypeGroups.push({ value: documentType.department?.code, label: label, items: [] })
          group = this.documentTypeGroups[count - 1]
        }

        // now the group is guaranteed to exist, append our item (documentType) to it in the syntax required by prime-ng
        const newItem: SelectItem = {
          label: documentType[this.filterLabelField],
          value: { code: documentType.code },
        }
        group.items.push(newItem)
      })
    })
  }

  /**
   * load list of countries for dropdown
   */
  loadCountries () {
    const params: CountriesListParams = { limit: 1000, is_acted: true, ordering: this.filterLabelField }
    this.countriesService.list(params).subscribe(
      res => {
        this.countries = res.results.map(country => ({ ...country, value: country.code_iso }))
      })
  }

  /**
   * load list of field offices for dropdown
   */
  loadPlaces () {
    this.placesApi.list({ limit: 1000, ordering: `admin_zone_2__admin_zone_1__country__${this.filterLabelField},name`, code__isnull: false }).subscribe(
      (res: PaginatedArchivesPlacesPlaceListList) => {
        this.places = res.results.map(place => ({
          ...place,
          label_en: place.name,
          label_fr: place.name,
          value: place.id,
        }))
      })
  }

  /**
   * load list of DocumentCategories for dropdown
   */
  loadDocumentCategories () {
    this.archiveDocumentsApi.listDocCategories({}).subscribe(
      (res: PaginatedArchiveDocumentCategoryList) => {
        this.documentCategories = res.results.map(category => ({
          ...category,
          value: category.code,
        }))
      })
  }

  /**
   * colour mandatory entries brown
   */
  getRowClass (item: ArchiveDocumentListItem): string {
    if (item.state === ArchiveDocumentListItemState.NEW) return 'info'
    return ''
  }

  onClickDownload () {
    // extract the filters currently used for this view, used along with the current locale to configure the export
    const filters = this.genericListComponent.getFilterParams()
    this.archiveDocumentsApi.export(filters, this.locale).subscribe(
      (res: LongRunningJob) => {
        this.longRunningJobService.addJob(res)
      })
  }

  countryForFlag (item: ArchiveDocumentListItem): CountryListItem | null {
    return item.country
  }

  getWorkflowState (item: ArchiveDocumentListItem): WorkflowState<ArchiveDocumentListItemState> {
    const workflowState = ARCHIVE_DOCUMENT_STATES.find(candidate => candidate.value === item.state) ?? ARCHIVE_DOCUMENT_STATES[0]
    return {
      id: 0,
      workflow: { id: 0, name: item.workflow, description: '' },
      name: workflowState.value,
      label_en: workflowState.label_en,
      label_fr: workflowState.label_fr,
      severity: toSeverity(workflowState.severity),
    }
  }
}

function toSeverity (severity: string): WorkflowState<ArchiveDocumentListItemState>['severity'] {
  switch (severity) {
    case 'success': return 'success'
    case 'secondary': return 'secondary'
    case 'warn': return 'warn'
    case 'danger': return 'danger'
    case 'contrast': return 'contrast'
    default: return 'info'
  }
}
